import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocAlert,
  DocLink,
  DocNavFooter,
  DocCard,
} from "@/components/docs";
import { ApiEndpoint, CodeBlock, Parameter } from "@/components/docs/api";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import {
  DocumentTextIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

export default function ApiStatusPagesPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "API Reference", href: "/docs/api" },
    { label: "Status Pages API" },
  ];

  const listStatusPagesQueryParams: Parameter[] = [
    {
      name: "page",
      type: "integer",
      required: false,
      description: "Page number for pagination",
      default: "1",
      example: "2",
    },
    {
      name: "per_page",
      type: "integer",
      required: false,
      description: "Items per page (max 100)",
      default: "20",
      example: "50",
    },
  ];

  const createStatusPageBodyParams: Parameter[] = [
    {
      name: "name",
      type: "string",
      required: true,
      description: "Display name for the status page",
      example: "Public Status Page",
    },
    {
      name: "monitors",
      type: "array",
      required: true,
      description: "Array of monitor IDs to display (min: 1, max: 20)",
      example: '["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"]',
    },
    {
      name: "period",
      type: "integer",
      required: false,
      description: "Days of history to show: 7, 30, or 90",
      default: "7",
      example: "30",
    },
    {
      name: "custom_domain",
      type: "string",
      required: false,
      description: "Custom domain for the status page",
      example: "status.example.com",
    },
    {
      name: "show_incident_reasons",
      type: "boolean",
      required: false,
      description: "Whether to show incident reasons publicly",
      default: "false",
      example: "true",
    },
    {
      name: "basic_auth",
      type: "string",
      required: false,
      description: 'Basic authentication credentials in "username:password" format',
      example: "viewer:secretpass",
    },
  ];

  const updateStatusPageBodyParams: Parameter[] = createStatusPageBodyParams.map((param) => ({
    ...param,
    required: false,
  }));

  return (
    <DocLayout
      title="Status Pages API - Uptime Monitor Documentation"
      description="API reference for managing public status pages in Uptime Monitor"
      canonical="https://uptime-monitor.io/docs/api/status-pages"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Status Pages API"
        description="Create and manage public status pages to share the health status of your monitored services with customers or team members."
      />

      <DocAlert variant="info" className="mb-8">
        <p>
          <strong>Base URL:</strong>{" "}
          <code className="font-mono">https://api.uptime-monitor.io/api</code>
        </p>
        <p className="mt-2">
          All endpoints require authentication using Bearer tokens with API keys. See{" "}
          <DocLink href="/docs/api/authentication">Authentication</DocLink> for details.
        </p>
        <p className="mt-2">
          Rate limits apply to all API endpoints (5000 points/hour). See{" "}
          <DocLink href="/docs/api/rate-limiting">Rate Limiting</DocLink> for details.
        </p>
      </DocAlert>

      {/* Overview */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-4`}>Overview</h2>

        <p className={`${docsColors.text.body} mb-6`}>
          The Status Pages API allows you to programmatically create and manage public status pages.
          Status pages provide real-time visibility into the health of your services, helping you
          communicate transparently with customers during incidents.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <DocCard className="p-6">
            <div className="flex items-start">
              <GlobeAltIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
              <div className="ml-4">
                <h3 className={docsComponents.sectionHeading}>Custom Domains</h3>
                <p className={docsColors.text.body}>
                  Use your own domain or our subdomain. Automatic HTTPS included.
                </p>
              </div>
            </div>
          </DocCard>

          <DocCard className="p-6">
            <div className="flex items-start">
              <ClockIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
              <div className="ml-4">
                <h3 className={docsComponents.sectionHeading}>Historical Data</h3>
                <p className={docsColors.text.body}>
                  Show 7, 30, or 90 days of uptime history with detailed metrics.
                </p>
              </div>
            </div>
          </DocCard>

          <DocCard className="p-6">
            <div className="flex items-start">
              <ShieldCheckIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
              <div className="ml-4">
                <h3 className={docsComponents.sectionHeading}>Access Control</h3>
                <p className={docsColors.text.body}>
                  Optional basic authentication to restrict access to authorized viewers.
                </p>
              </div>
            </div>
          </DocCard>

          <DocCard className="p-6">
            <div className="flex items-start">
              <DocumentTextIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
              <div className="ml-4">
                <h3 className={docsComponents.sectionHeading}>Incident Details</h3>
                <p className={docsColors.text.body}>
                  Choose whether to display incident reasons to build customer trust.
                </p>
              </div>
            </div>
          </DocCard>
        </div>
      </section>

      {/* Endpoints */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>Endpoints</h2>

        {/* List Status Pages */}
        <ApiEndpoint
          method="GET"
          path="/api/status_pages"
          description="Retrieve a paginated list of all status pages in your account"
          queryParameters={listStatusPagesQueryParams}
          requestExample={`curl -X GET "https://api.uptime-monitor.io/api/status_pages?page=1&per_page=20" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"`}
          responseExample={`{
  "status": "ok",
  "data": {
    "status_pages": [
      {
        "id": "507f1f77bcf86cd799439011",
        "name": "Public Status Page",
        "monitors": ["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"],
        "period": 7,
        "custom_domain": "status.example.com",
        "show_incident_reasons": true,
        "basic_auth": "user:password",
        "created_at": 1704067200,
        "url": "https://status.example.com"
      }
    ],
    "pagination": {
      "page": 1,
      "per_page": 20,
      "total": 5,
      "total_pages": 1,
      "has_next": false,
      "has_prev": false
    }
  }
}`}
        />

        {/* Get Status Page */}
        <ApiEndpoint
          method="GET"
          path="/api/status_pages/:id"
          description="Retrieve details of a specific status page"
          requestExample={`curl -X GET "https://api.uptime-monitor.io/api/status_pages/507f1f77bcf86cd799439011" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"`}
          responseExample={`{
  "status": "ok",
  "data": {
    "status_page": {
      "id": "507f1f77bcf86cd799439011",
      "name": "Public Status Page",
      "monitors": ["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"],
      "period": 7,
      "custom_domain": "status.example.com",
      "show_incident_reasons": true,
      "basic_auth": "user:password",
      "created_at": 1704067200,
      "url": "https://status.example.com"
    }
  }
}`}
        />

        {/* Create Status Page */}
        <ApiEndpoint
          method="POST"
          path="/api/status_pages"
          description="Create a new status page"
          bodyParameters={createStatusPageBodyParams}
          requestExample={`curl -X POST "https://api.uptime-monitor.io/api/status_pages" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Public Status Page",
    "monitors": ["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"],
    "period": 30,
    "custom_domain": "status.mycompany.com",
    "show_incident_reasons": true,
    "basic_auth": "viewer:secretpass"
  }'`}
          responseExample={`{
  "status": "ok",
  "data": {
    "status_page": {
      "id": "507f1f77bcf86cd799439011",
      "name": "My Public Status Page",
      "monitors": ["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"],
      "period": 30,
      "custom_domain": "status.mycompany.com",
      "show_incident_reasons": true,
      "basic_auth": "viewer:secretpass",
      "created_at": 1704067200,
      "url": "https://status.mycompany.com"
    }
  }
}`}
        />

        {/* Update Status Page */}
        <ApiEndpoint
          method="PUT"
          path="/api/status_pages/:id"
          description="Update an existing status page. All fields are optional."
          bodyParameters={updateStatusPageBodyParams}
          requestExample={`curl -X PUT "https://api.uptime-monitor.io/api/status_pages/507f1f77bcf86cd799439011" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Updated Status Page Name",
    "monitors": ["507f1f77bcf86cd799439014"],
    "period": 90,
    "show_incident_reasons": false,
    "basic_auth": null
  }'`}
          responseExample={`{
  "status": "ok",
  "data": {
    "status_page": {
      "id": "507f1f77bcf86cd799439011",
      "name": "Updated Status Page Name",
      "monitors": ["507f1f77bcf86cd799439014"],
      "period": 90,
      "custom_domain": "status.mycompany.com",
      "show_incident_reasons": false,
      "basic_auth": null,
      "created_at": 1704067200,
      "url": "https://status.mycompany.com"
    }
  }
}`}
        />

        {/* Delete Status Page */}
        <ApiEndpoint
          method="DELETE"
          path="/api/status_pages/:id"
          description="Permanently delete a status page"
          requestExample={`curl -X DELETE "https://api.uptime-monitor.io/api/status_pages/507f1f77bcf86cd799439011" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"`}
          responseExample={`{
  "status": "ok",
  "data": {
    "message": "Status page deleted successfully"
  }
}`}
        />
      </section>

      {/* Field Details */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>Field Details</h2>

        <div className="space-y-6">
          <div>
            <h3 className={docsComponents.sectionHeading}>Period Values</h3>
            <ul className={`${docsColors.text.body} space-y-2 ml-4`}>
              <li>
                • <code>7</code> - Show 7 days of uptime history (default)
              </li>
              <li>
                • <code>30</code> - Show 30 days of uptime history
              </li>
              <li>
                • <code>90</code> - Show 90 days of uptime history
              </li>
            </ul>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>URL Generation</h3>
            <p className={`${docsColors.text.body} mb-2`}>
              The status page URL is automatically generated based on your configuration:
            </p>
            <ul className={`${docsColors.text.body} space-y-2 ml-4`}>
              <li>
                • If <code>custom_domain</code> is set: <code>https://{"{custom_domain}"}</code>
              </li>
              <li>
                • Otherwise: <code>https://status.uptime-monitor.io/{"{id}"}</code>
              </li>
            </ul>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>Custom Domain Requirements</h3>
            <ul className={`${docsColors.text.body} space-y-2 ml-4`}>
              <li>• Must be a valid domain name</li>
              <li>• Cannot end with &ldquo;uptime-monitor.io&rdquo;</li>
              <li>• Must contain at least one dot (no top-level domains)</li>
              <li>• Cannot contain forward slashes</li>
              <li>• Domain must be configured to point to our servers</li>
            </ul>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>Basic Authentication</h3>
            <p className={`${docsColors.text.body} mb-2`}>
              Protect your status page with basic authentication:
            </p>
            <ul className={`${docsColors.text.body} space-y-2 ml-4`}>
              <li>
                • Format: <code>username:password</code>
              </li>
              <li>
                • Set to <code>null</code> to disable authentication
              </li>
              <li>• When enabled, visitors must authenticate to view the status page</li>
            </ul>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>Monitor Selection</h3>
            <ul className={`${docsColors.text.body} space-y-2 ml-4`}>
              <li>• Minimum 1 monitor, maximum 20 monitors per status page</li>
              <li>• Monitor order in the array determines display order</li>
              <li>• All monitors must belong to your account</li>
              <li>
                • Removing a monitor from your account automatically removes it from status pages
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Error Responses */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>
          Error Responses
        </h2>

        <div className="space-y-4">
          <DocCard className="p-4">
            <h4 className="font-semibold text-red-600 mb-2">400 Bad Request</h4>
            <CodeBlock language="json">
              {`{
  "status": "error",
  "error": "Invalid status page ID",
  "error_code": "VALIDATION_ERROR"
}`}
            </CodeBlock>
            <p className={`${docsColors.text.muted} text-sm mt-2`}>Common validation errors:</p>
            <ul className={`${docsColors.text.muted} text-sm space-y-1 ml-4 mt-1`}>
              <li>• &ldquo;You must select at least one monitor&rdquo;</li>
              <li>• &ldquo;Maximum 20 monitors allowed per status page&rdquo;</li>
              <li>• &ldquo;Invalid domain name&rdquo;</li>
              <li>• &ldquo;You cannot use uptime-monitor.io domain here&rdquo;</li>
              <li>
                • &ldquo;Monitor {"{id}"} not found or doesn&apos;t belong to your account&rdquo;
              </li>
            </ul>
          </DocCard>

          <DocCard className="p-4">
            <h4 className="font-semibold text-red-600 mb-2">404 Not Found</h4>
            <CodeBlock language="json">
              {`{
  "status": "error",
  "error": "Status page not found",
  "error_code": "STATUS_PAGE_NOT_FOUND"
}`}
            </CodeBlock>
          </DocCard>

          <DocCard className="p-4">
            <h4 className="font-semibold text-red-600 mb-2">429 Too Many Requests</h4>
            <CodeBlock language="json">
              {`{
  "error": "rate_limit_exceeded",
  "message": "Too many requests. Please retry later.",
  "retry_after": 3600
}`}
            </CodeBlock>
          </DocCard>
        </div>
      </section>

      {/* Examples */}
      <section className="mb-12">
        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-6`}>Examples</h2>

        <div className="space-y-6">
          <div>
            <h3 className={docsComponents.sectionHeading}>Create a Simple Status Page</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Create a basic status page with default settings:
            </p>
            <CodeBlock language="bash">
              {`curl -X POST "https://api.uptime-monitor.io/api/status_pages" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Service Status",
    "monitors": ["507f1f77bcf86cd799439012"]
  }'`}
            </CodeBlock>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>
              Create a Status Page with Custom Domain
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Create a fully customized status page with all options:
            </p>
            <CodeBlock language="bash">
              {`curl -X POST "https://api.uptime-monitor.io/api/status_pages" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Company Status Page",
    "monitors": ["507f1f77bcf86cd799439012", "507f1f77bcf86cd799439013"],
    "period": 30,
    "custom_domain": "status.mycompany.com",
    "show_incident_reasons": true,
    "basic_auth": "viewer:secretpass"
  }'`}
            </CodeBlock>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>Update Monitor Selection</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Change which monitors are displayed on the status page:
            </p>
            <CodeBlock language="bash">
              {`curl -X PUT "https://api.uptime-monitor.io/api/status_pages/507f1f77bcf86cd799439011" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "monitors": ["507f1f77bcf86cd799439014", "507f1f77bcf86cd799439015"]
  }'`}
            </CodeBlock>
          </div>

          <div>
            <h3 className={docsComponents.sectionHeading}>Remove Basic Authentication</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Make a status page publicly accessible by removing authentication:
            </p>
            <CodeBlock language="bash">
              {`curl -X PUT "https://api.uptime-monitor.io/api/status_pages/507f1f77bcf86cd799439011" \\
  -H "Authorization: Bearer um_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "basic_auth": null
  }'`}
            </CodeBlock>
          </div>
        </div>
      </section>

      <DocNavFooter
        prev={{ href: "/docs/api/incidents", text: "Incidents API" }}
        next={{ href: "/docs/api/rate-limiting", text: "Rate Limiting" }}
        className="mt-12"
      />
    </DocLayout>
  );
}
